import React from "react";
import "./VolunteerGroup.css";

interface FunctionDto {
    functionId: number;
    functionName: string;
    eventID: number;
}

interface VolunteerGroup {
    id?: number;
    name: string;
    type: string;
    eventId: number;
    function?: string;
    maxSlots: number;
    contactPersonId?: number;
    selfAssignShifts: boolean;
    deadline?: string;
}

interface EventDto {
    eventID: number;
    eventName: string;
}

interface PersonDto {
    personId: number;
    firstName: string;
    lastName: string;
}

interface Props {
    open: boolean;
    form: VolunteerGroup;
    events: EventDto[];
    persons: any[];
    functions: FunctionDto[];
    editingId: number | null;
    setForm: React.Dispatch<React.SetStateAction<VolunteerGroup>>;
    onClose: () => void;
    onSave: () => void;

    saveError?: string | null;
    saving?: boolean;
}

const VolunteerGroupModal: React.FC<Props> = ({
    open,
    form,
    functions,
    events,
    persons,
    editingId,
    onClose,
    onSave,
    setForm,
    saveError,
    saving,

}) => {
    const [personSearch, setPersonSearch] = React.useState("");
    const [personOpen, setPersonOpen] = React.useState(false);

    const filteredFunctions = functions.filter(
        (f: FunctionDto) => f.eventID === form.eventId
    );

    const filteredPersons = (persons ?? []).filter((p) =>
        `${p.firstName} ${p.lastName}`
            .toLowerCase()
            .includes(personSearch.toLowerCase())
    );

    React.useEffect(() => {
        const selected = persons.find((p) => p.personId === form.contactPersonId);
        if (selected) {
            setPersonSearch(`${selected.firstName} ${selected.lastName}`);
        }
    }, [form.contactPersonId, persons]);

    if (!open) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-dialog persons-modal">

                <h2>
                    {editingId ? "Rediger gruppe" : "Opret gruppe"}
                </h2>

                {/* NEW WRAPPER */}
                <div className="personsFormBody">

                    {/* FORM GRID */}
                    <div className="personsAdvancedGrid">

                        <div className="personsField">
                            <label className="personsLabel">Navn</label>
                            <input
                                className="personsControl"
                                value={form.name}
                                onChange={(e) =>
                                    setForm({ ...form, name: e.target.value })
                                }
                            />
                        </div>

                        <div className="personsField">
                            <label className="personsLabel">Type</label>
                            <select
                                className="personsControl"
                                value={form.type}
                                onChange={(e) =>
                                    setForm({ ...form, type: e.target.value })
                                }
                            >
                                <option value="">Vælg type</option>
                                <option value="Efterskoler">Efterskoler</option>
                                <option value="Intern">Intern</option>
                                <option value="Forening">Forening</option>
                                <option value="Andet">Andet</option>
                            </select>
                        </div>

                        <div className="personsField">
                            <label className="personsLabel">Event</label>
                            <select
                                className="personsControl"
                                value={form.eventId}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        eventId: Number(e.target.value),
                                    })
                                }
                            >
                                <option value={0}>Vælg event</option>
                                {events.map((e) => (
                                    <option key={e.eventID} value={e.eventID}>
                                        {e.eventName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="personsField">
                            <label className="personsLabel">Funktion (valgfri)</label>
                            <select
                                className="personsControl"
                                value={form.function || ""}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        function: e.target.value,
                                    })
                                }
                            >
                                <option value="">Ingen funktion</option>

                                {filteredFunctions.map((f: FunctionDto) => (
                                    <option key={f.functionId} value={f.functionName}>
                                        {f.functionName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="personsField">
                            <label className="personsLabel">Maks pladser</label>
                            <input
                                className="personsControl"
                                type="number"
                                value={form.maxSlots}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        maxSlots: Number(e.target.value),
                                    })
                                }
                            />
                        </div>

                        <div className="personsField">
                            <label className="personsLabel">Kontaktperson</label>

                            <div style={{ position: "relative" }}>
                                <input
                                    className="personsControl"
                                    placeholder="Søg efter person..."
                                    value={personSearch}
                                    onFocus={() => setPersonOpen(true)}
                                    onChange={(e) => {
                                        setPersonSearch(e.target.value);
                                        setPersonOpen(true);
                                    }}
                                />

                                {personOpen && filteredPersons.length > 0 && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "100%",
                                            left: 0,
                                            right: 0,
                                            background: "white",
                                            border: "1px solid #ccc",
                                            maxHeight: 200,
                                            overflowY: "auto",
                                            zIndex: 9999,
                                        }}
                                    >
                                        {filteredPersons.map((p) => (
                                            <div
                                                key={p.personId}
                                                onClick={() => {
                                                    setForm({
                                                        ...form,
                                                        contactPersonId: p.personId,
                                                    });

                                                    setPersonSearch(
                                                        `${p.firstName} ${p.lastName}`
                                                    );
                                                    setPersonOpen(false);
                                                }}
                                                style={{
                                                    padding: 10,
                                                    cursor: "pointer",
                                                }}
                                            >
                                                {p.firstName} {p.lastName}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="personsField">
                            <label className="personsLabel">Deadline</label>
                            <input
                                className="personsControl"
                                type="datetime-local"
                                value={form.deadline || ""}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        deadline: e.target.value,
                                    })
                                }
                            />
                        </div>

                    </div>

                    {/* CHECKBOX */}
                    <label className="personsCheckbox">
                        <input
                            type="checkbox"
                            checked={form.selfAssignShifts}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    selfAssignShifts: e.target.checked,
                                })
                            }
                        />
                        Selvtilmelding til vagter
                    </label>
                    {saveError && (
                        <div className="volunteer-group-error">
                            <div className="volunteer-group-error-icon">
                                !
                            </div>

                            <div>
                                <strong>Gruppen kunne ikke gemmes</strong>

                                {saveError.split("\n").map((line, index) => (
                                    <div key={index}>
                                        {line}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {/* ACTIONS */}
                    <div className="personsActionsRow">
                        <button className="btn btn-secondary" onClick={onClose}>
                            Annuller
                        </button>

                        <button className="btn btn-success" onClick={onSave}>
                            {editingId ? "Opdater" : "Opret"}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default VolunteerGroupModal;